/**
 * Reads the assistant reply clip's own playback amplitude, so the room's light can track what
 * is actually audible ("Gemma talking") rather than merely "a clip is open" — the fix from a
 * second real headset session, which found the 'speaking' state static (the session after the
 * one that got the light rendering and coloured at all in the first place, plan/ai-in-room.md,
 * "Fix 2"). An `AnalyserNode` sampled on a plain `setInterval`, not `requestAnimationFrame` —
 * this has to keep sampling even inside an active WebXR immersive session, where the *page's
 * own* `window.requestAnimationFrame` is throttled by the browser because the 2D document is
 * not what is being shown to the user (Babylon's own render loop sidesteps this by switching to
 * `XRSession.requestAnimationFrame` once a session is active, which this class has no access to
 * and no reason to duplicate) — see CLAUDE.md, "`requestAnimationFrame` is throttled inside an
 * active WebXR session," the same reasoning applied to output instead of input.
 *
 * **The one real gotcha, worth stating plainly rather than discovering by a silent playback
 * failure:** `AudioContext.createMediaElementSource(audio)` reroutes the element's *entire*
 * output through the Web Audio graph the instant it is called. From that point on, `audio` is
 * audible only if something downstream of the source node reaches `audioContext.destination`.
 * Tapping the signal for analysis and forgetting to also route it to the destination would not
 * fail loudly — it would silently mute every assistant reply, a far worse regression than the
 * static light this class exists to fix. `start()` below always wires
 * `source -> analyser -> destination`, never `source -> analyser` alone.
 *
 * Every step in `start()` is wrapped in one try/catch: this is best-effort instrumentation for
 * a light, layered on top of the reply clip's own playback, and a failure here (an unsupported
 * `AudioContext`, a context that never resumes, anything) must never prevent or interrupt the
 * clip actually playing — `AssistantController.handleReply`'s own event wiring does not depend
 * on this class succeeding, only on `audio.play()` itself.
 */
export class PlaybackAmplitude {
  private audioContext: AudioContext | null = null;
  private intervalHandle: ReturnType<typeof setInterval> | null = null;

  /**
   * Starts sampling `audio`'s own output amplitude (RMS, 0..1) every `SAMPLE_INTERVAL_MS`,
   * reported through `onSample`, until `stop()` is called. Stops any previous tracking first,
   * so a caller never has to remember to pair every `start()` with a `stop()` of its own before
   * the next one.
   */
  start(audio: HTMLAudioElement, onSample: (level: number) => void): void {
    this.stop();

    try {
      const audioContext = new AudioContext();
      const source = audioContext.createMediaElementSource(audio);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;

      source.connect(analyser);
      analyser.connect(audioContext.destination);

      // Best-effort: browsers may start a freshly-constructed AudioContext suspended until a
      // user gesture unblocks it. Every path that can reach a reply clip at all (joining a
      // room, entering VR, the splash's own buttons) already carries a genuine gesture earlier
      // in the same page, so this reliably resolves in practice — but nothing here depends on
      // it settling before the interval below starts; a still-suspended context just reads
      // silence until it does.
      void audioContext.resume().catch(() => {});

      const samples = new Uint8Array(analyser.fftSize);

      this.audioContext = audioContext;
      this.intervalHandle = setInterval(() => {
        analyser.getByteTimeDomainData(samples);
        onSample(rmsOfByteSamples(samples));
      }, SAMPLE_INTERVAL_MS);
    } catch (error) {
      console.warn('Assistant: failed to set up playback amplitude tracking.', error);
    }
  }

  /** A no-op if not currently tracking. */
  stop(): void {
    if (this.intervalHandle !== null) {
      clearInterval(this.intervalHandle);
      this.intervalHandle = null;
    }

    if (this.audioContext) {
      void this.audioContext.close().catch(() => {});
      this.audioContext = null;
    }
  }
}

/** How often to sample the clip's own amplitude — a fine enough step that it feeds a visual
 *  animation someone is looking at without reading as stutter. */
const SAMPLE_INTERVAL_MS = 50;

/**
 * Root-mean-square of raw 8-bit time-domain samples (128 = silence), the same five-line
 * computation `voice/VoiceBands.ts`'s own `rmsOfTimeDomainBytes` performs over a peer's live
 * voice playback. Factored out as a pure function so it is something this project's test suite
 * can actually exercise directly (jsdom has no Web Audio implementation; see this file's own
 * module doc). Not shared with that file: it analyses a peer's voice for a visor animation,
 * this one analyses the assistant's own reply clip for a light's, and the two have nothing in
 * common beyond this five-line formula, which is short enough that sharing it would
 * cost more (an import across two otherwise-unrelated concerns) than it would save.
 */
export function rmsOfByteSamples(samples: Uint8Array): number {
  let sumSquares = 0;

  for (let i = 0; i < samples.length; i++) {
    const centred = (samples[i]! - 128) / 128;
    sumSquares += centred * centred;
  }

  return Math.sqrt(sumSquares / samples.length);
}
